/**
 * Identité d'une gamme à l'écran : libellé, références constructeur, critères de suggestion.
 *
 * Fonctions pures, sans React : c'est ici que se décide ce qui part vers l'API et ce que l'écran
 * affiche, et c'est donc ici que cela se teste.
 *
 * Un critère est un modèle de machine (et, si besoin, une référence constructeur) pour lequel on
 * PROPOSE la gamme. L'API compare par une clé normalisée (« DD 35+ » et « dd35+ » sont le même
 * modèle) et conserve le texte tel que saisi pour l'affichage ; un critère créé avant que ce
 * texte soit conservé n'a que sa clé, qui sert alors de texte.
 */
import type { GammeCritere } from '../api/client';

/** Une ligne de la liste de critères en cours de saisie. */
export interface LigneCritere {
  /** Identifiant local, stable : sert de clé React, jamais envoyé à l'API. */
  readonly id: number;
  readonly modele: string;
  readonly reference: string;
}

/** Un critère tel que l'API l'attend. */
export interface CritereEnvoye {
  readonly model: string;
  readonly manufacturerReference?: string;
}

let sequence = 0;

export function ligneCritereVide(): LigneCritere {
  sequence += 1;
  return { id: sequence, modele: '', reference: '' };
}

/** Une ligne de critère déjà remplie : par exemple le modèle et la référence d'une machine. */
export function ligneCritereDe(modele: string, reference: string): LigneCritere {
  sequence += 1;
  return { id: sequence, modele, reference };
}

/**
 * Le code proposé pour une gamme copiée d'une autre : celui de l'originale suivi de « _2 »
 * (« KAESER_DSDX302 » → « KAESER_DSDX302_2 »), ou le numéro suivant si elle en porte déjà un
 * (« …_2 » → « …_3 »). Toujours modifiable : le serveur refuse un code déjà pris. Borné à 60
 * caractères, la longueur maximale d'un code.
 */
export function codeDeCopie(code: string): string {
  const suite = /^(.*)_(\d+)$/.exec(code);
  const base = suite?.[1];
  const numero = suite?.[2];
  const propose = base !== undefined && numero !== undefined ? `${base}_${Number(numero) + 1}` : `${code}_2`;
  if (propose.length <= 60) return propose;
  const suffixe = propose.slice(propose.lastIndexOf('_'));
  return `${propose.slice(0, 60 - suffixe.length)}${suffixe}`;
}

/** Les lignes de saisie d'une gamme existante : le texte saisi, ou à défaut la clé. */
export function lignesDepuisGamme(criteres: readonly GammeCritere[]): LigneCritere[] {
  return criteres.map((c) => {
    sequence += 1;
    return {
      id: sequence,
      modele: c.modelLabel ?? c.modelKey,
      reference: c.manufacturerReferenceLabel ?? c.manufacturerReferenceKey,
    };
  });
}

/** Ce qui s'affiche d'un critère : « DD35+ », ou « GA75 (réf. AB.1) » s'il porte une référence. */
export function libelleCritere(c: GammeCritere): string {
  const modele = c.modelLabel ?? c.modelKey;
  const reference = c.manufacturerReferenceLabel ?? c.manufacturerReferenceKey;
  return reference === '' ? modele : `${modele} (réf. ${reference})`;
}

/** Les critères à envoyer : lignes entièrement vides écartées, textes rognés, référence blanche omise. */
export function criteresPourEnvoi(lignes: readonly LigneCritere[]): CritereEnvoye[] {
  return lignes
    .map((l) => ({ model: l.modele.trim(), reference: l.reference.trim() }))
    .filter((l) => l.model !== '' || l.reference !== '')
    .map((l) => ({
      model: l.model,
      ...(l.reference === '' ? {} : { manufacturerReference: l.reference }),
    }));
}

/** Une référence par ligne, ou séparées par des virgules ou des points-virgules. */
export function lireReferences(texte: string): string[] {
  return texte
    .split(/[\n,;]+/)
    .map((r) => r.trim())
    .filter((r) => r !== '');
}

export function texteReferences(references: readonly string[]): string {
  return references.join('\n');
}

/** Deux textes identiques à la casse et aux espaces superflus près. */
function memeTexte(a: string, b: string): boolean {
  const normaliser = (t: string) => t.trim().replace(/\s+/g, ' ').toLowerCase();
  return normaliser(a) === normaliser(b);
}

/**
 * Premier défaut de saisie, en français, ou `null` si tout est présentable. Le serveur re-vérifie
 * tout : ces messages évitent seulement un aller-retour pour les erreurs évidentes.
 */
export function validerIdentite(saisie: {
  readonly libelle: string;
  /** `undefined` en modification : la marque d'une gamme ne change pas. */
  readonly marqueId?: string | null;
  readonly lignes: readonly LigneCritere[];
  /**
   * Libellé de la gamme dont on fait une copie : la copie doit s'en distinguer, sans quoi la liste
   * montrerait deux gammes de même nom et on ne saurait plus laquelle choisir.
   */
  readonly libelleOriginal?: string;
}): string | null {
  if (saisie.libelle.trim() === '') return 'Le libellé de la gamme est obligatoire.';
  if (saisie.libelleOriginal !== undefined && memeTexte(saisie.libelle, saisie.libelleOriginal)) {
    return 'Changez le libellé : il doit distinguer cette copie de la gamme d’origine (par exemple en y mettant la nouvelle référence).';
  }
  if (saisie.marqueId !== undefined && !saisie.marqueId) return 'Choisissez la marque (le constructeur).';

  const criteres = criteresPourEnvoi(saisie.lignes);
  if (criteres.length === 0) {
    return 'Indiquez au moins un modèle de machine : c’est lui qui fait proposer la gamme.';
  }
  if (criteres.some((c) => c.model === '')) {
    return 'Un critère porte une référence constructeur mais pas de modèle.';
  }
  const vus = new Set<string>();
  for (const c of criteres) {
    const cle = `${c.model.toUpperCase().replace(/\s+/g, '')}|${(c.manufacturerReference ?? '').toUpperCase().replace(/\s+/g, '')}`;
    if (vus.has(cle)) return `Le critère « ${c.model} » est saisi deux fois.`;
    vus.add(cle);
  }
  return null;
}

/**
 * Ce qu'on annonce une fois une gamme créée à partir d'une autre : d'où vient son contenu, et la suite
 * à donner. Un seul texte pour la page Gammes et pour la fiche machine.
 */
export function messageGammeCopiee(nouvelle: string, origine: string): string {
  return (
    `Gamme « ${nouvelle} » créée à partir de « ${origine} » : son brouillon de version 1 reprend le contenu et les prix. ` +
    'Modifiez-le avec « Modifier le contenu », puis publiez-le : les machines correspondantes apparaîtront alors en bas de page.'
  );
}
